import { useEffect, useState } from "react";
import { X, ExternalLink, Sun } from "lucide-react";
import { useI18n } from "@/context/I18n";
import { useMusicPlayer } from "@/context/MusicPlayerContext";
import { isIos } from "@/components/InstallPrompt";

const DISMISSED_KEY = "sonlyo.bgaudio.dismissed";
const PLAY_STORE = "https://play.google.com/store/apps/details?id=com.brave.browser";

/**
 * Opens SONLYO in Brave, or sends the listener to install it.
 *
 * An Android intent link does both in one tap: it hands the URL to Brave when
 * it is installed, and falls back to the store listing when it is not. A web
 * page cannot install an app itself, so the store is as far as this can go.
 */
function openInBrave() {
  const target = `${window.location.host}${window.location.pathname}`;
  const url =
    `intent://${target}#Intent;scheme=https;package=com.brave.browser;` +
    `S.browser_fallback_url=${encodeURIComponent(PLAY_STORE)};end`;
  window.location.href = url;
}

export function isAndroid() {
  return /android/i.test(window.navigator.userAgent);
}

/**
 * Says what happens to the music when the listener leaves, and offers the
 * best thing available on their phone.
 *
 * Leaving the app stops playback: the browser pauses the player once the page
 * is no longer visible, and the page cannot argue with it. What can be done
 * about that splits by platform, so the notice does too.
 *
 * On Android there is a real fix. Brave carries a setting that keeps playback
 * alive and it works here, so the banner offers a tap into Brave.
 *
 * On iOS there is not one, and pretending otherwise would be worse than
 * saying nothing. Every iOS browser is required to run on WebKit, so Brave
 * there is Safari's engine wearing a different coat and carries none of the
 * patch that makes the Android version work. The honest offer is the screen
 * wake lock: it does not allow other apps, it stops the screen locking, which
 * is what silently ends a listening session left on the table. Safari has
 * supported it since 16.4; older iPhones report it missing and are told
 * nothing rather than sold a setting they do not have.
 */
export default function BackgroundAudioNotice() {
  const { t } = useI18n();
  const { currentTrack, videoMode, keepAwake, setKeepAwake, keepAwakeSupported } = useMusicPlayer();
  const [isBrave, setIsBrave] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISSED_KEY) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    // Brave says so itself; the user agent deliberately does not.
    if (navigator.brave && navigator.brave.isBrave) {
      navigator.brave
        .isBrave()
        .then((v) => setIsBrave(!!v))
        .catch(() => setIsBrave(false));
    } else {
      setIsBrave(false);
    }
  }, []);

  const close = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISSED_KEY, "1");
    } catch {
      /* private mode */
    }
  };

  // Only once there is something playing: the warning means nothing to
  // someone who has not put music on yet, and it keeps the banner out of the
  // way of the install prompt a first-time listener sees.
  if (dismissed || !currentTrack || videoMode) return null;

  const onIos = isIos();
  const androidNeedsBrave = isAndroid() && isBrave === false;
  const iosCanHoldScreen = onIos && keepAwakeSupported && !keepAwake;
  if (!androidNeedsBrave && !iosCanHoldScreen) return null;

  return (
    <div className="fixed bottom-[146px] left-3 right-3 z-50 md:hidden" data-testid="bgaudio-banner">
      <div className="bg-[#141414] border border-white/10 rounded-2xl shadow-2xl p-3">
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <p className="font-heading font-bold text-sm leading-tight">
              {onIos ? t("bg_ios_title") : t("brave_title")}
            </p>
            <p className="text-xs text-zinc-400 leading-snug mt-0.5">
              {onIos ? t("bg_ios_sub") : t("brave_sub")}
            </p>
          </div>
          <button onClick={close} aria-label="Close" className="text-zinc-500 hover:text-white p-1 shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
        <button
          data-testid="bgaudio-banner-btn"
          onClick={
            onIos
              ? () => {
                  setKeepAwake(true);
                  close();
                }
              : openInBrave
          }
          className="mt-2.5 w-full bg-[#FF2A4D] hover:bg-[#E61C3D] rounded-full px-4 py-2 text-sm font-semibold flex items-center justify-center gap-1.5"
        >
          {onIos ? (
            <>
              <Sun className="w-4 h-4" /> {t("bg_ios_action")}
            </>
          ) : (
            <>
              <ExternalLink className="w-4 h-4" /> {t("brave_open")}
            </>
          )}
        </button>
      </div>
    </div>
  );
}
